import React from 'react'
import { Link, Outlet } from 'react-router-dom'
import { Backdrop, styled, Typography, Stack, Button } from '@mui/material'
import Divider from '@mui/material/Divider'

//components
import MenuItem from './MenuItem'

import RefreshDataButton from '../components/RefreshData/RefreshDataButton'

//assets
import Logo from '../assets/logo.png'
import { SIDE_PANEL_WIDTH } from '../utils/constants'

import HomeIcon from '@mui/icons-material/Home'
import BackupTableIcon from '@mui/icons-material/BackupTable'
import RssFeedIcon from '@mui/icons-material/RssFeed'
import HandymanIcon from '@mui/icons-material/Handyman'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import BarChartIcon from '@mui/icons-material/BarChart'
import EngineeringIcon from '@mui/icons-material/Engineering'
import AssessmentIcon from '@mui/icons-material/Assessment'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import MoreTimeIcon from '@mui/icons-material/MoreTime'

import DashboardGlobalStateContext from '../context/DashboardGlobalStateContext'
import LoadingSpinner from '../components/LoadingSpinner'
import { useAuth0 } from '@auth0/auth0-react'

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.drawer + 1,
  background: 'transparent',
  backgroundColor: 'rgba(0,0,0,0.1)',
  color: theme.palette.secondary.main
}))

const App = styled('main')(() => ({
  width: '100%',
  height: '100vh',
  display: 'flex',
  color: '#424242',
  backgroundColor: '#fff'
}))

const SidePanel = styled('div')(() => ({
  width: `${SIDE_PANEL_WIDTH}px`,
  height: '100vh',
  borderRight: '1px solid #dedede',
  backgroundColor: '#113969'
}))

const SidePanelInnerDiv = styled('div')(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px'
}))

const MainContent = styled('div')(() => ({
  width: `calc(100% - ${SIDE_PANEL_WIDTH}px)`,
  width: '100%',
  height: '100vh',
  overflowY: 'hidden'
}))

const LogoWrapper = styled('img')(() => ({
  maxWidth: '100%',
  borderRadius: '8px'
}))

const LogoBox = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '0 16px',
  marginTop: 'auto',
  borderRadius: '8px'
}))

const NavBox = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  gap: '8px',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  marginTop: '12px'
}))

const MENU_ICON_FONT = 'small'
const SidebarData = [
  {
    title: 'Home',
    path: '/',
    renderIcon: () => {
      return <HomeIcon fontSize={MENU_ICON_FONT} />
    }
  },
  {
    title: 'Dashboard',
    path: '/dashboard',
    renderIcon: () => {
      return <BackupTableIcon fontSize={MENU_ICON_FONT} />
    }
  },
  {
    title: 'Live data',
    path: '/dashboard/live-data',
    renderIcon: () => {
      return <RssFeedIcon fontSize={MENU_ICON_FONT} />
    }
  },
  {
    title: 'Machine wise',
    path: '/dashboard/machine-wise',
    renderIcon: () => {
      return <PrecisionManufacturingIcon fontSize={MENU_ICON_FONT} />
    }
  },
  {
    title: 'Final inspection',
    path: '/dashboard/final-inspection',
    renderIcon: () => {
      return <PrecisionManufacturingIcon fontSize={MENU_ICON_FONT} />
    }
  },
  {
    title: 'Uptime',
    path: '/dashboard/machine-maintenance',
    renderIcon: () => {
      return <MoreTimeIcon fontSize={MENU_ICON_FONT} />
    }
  },
  {
    title: 'Power BI',
    path: '/dashboard/power-bi-report',
    renderIcon: () => {
      return <AssessmentIcon fontSize={MENU_ICON_FONT} />
    },
    onClick: () => {
      const redirectUrl = `${process.env.REACT_APP_DATA_LOGGER_BASE_URL}/v1/power-bi-report/redirect`
      window.open(redirectUrl, '_blank')
    }
  }
]

const historicalData = [
  {
    title: 'Historical',
    path: '/dashboard/historical',
    renderIcon: () => {
      return <BarChartIcon fontSize="medium" />
    }
  },
  {
    title: 'Operator info',
    path: '/dashboard/operator-info',
    renderIcon: () => {
      return <EngineeringIcon fontSize="medium" />
    }
  }
]

const DashboardLayout = () => {
  const { logout } = useAuth0()
  const [machinesList, setMachinesList] = React.useState([])
  const [fetchingMachineState, setFetchingMachineState] = React.useState('idle')

  React.useEffect(() => {
    setFetchingMachineState('fetching')
    fetch(`${process.env.REACT_APP_ADMIN_BASE_URL}/v1/machines`)
      .then((response) => response.json())
      .then((data) => {
        const machineOptions = data.data.map((item) => ({
          value: item.name,
          label: item.displayName
        }))
        setMachinesList(machineOptions)
      })
      .catch((error) => {
        console.error('Error fetching machines:', error)
        setFetchingMachineState('failed')
      })
      .finally(() => {
        setFetchingMachineState('finished')
      })
  }, [])

  const loadingState = ['idle', 'fetching'].includes(fetchingMachineState)

  if (loadingState) {
    return <LoadingSpinner loading={loadingState} text="Loading data..." />
  }

  if (!loadingState && machinesList.length === 0) {
    return (
      <StyledBackdrop open={true}>
        <Stack
          sx={{
            backgroundColor: '#fffbfbff',
            padding: '24px',
            borderRadius: '8px',
            gap: '12px',
            alignItems: 'center'
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: '48px', color: 'red' }} />
          <Stack>
            <Typography variant="body1">
              We encountered an unexpected issue while loading the interface.{' '}
            </Typography>
            <ol
              style={{
                listStylePosition: 'inside'
              }}
            >
              Please try the following steps:
              <li>Refresh the page </li>
              <li>Logout and try logging in again </li>
              <li>Clear your browser cache and try again</li>
              <li>Wait a few minutes and retry</li>
            </ol>
          </Stack>
          <Stack sx={{ flexDirection: 'row', gap: '12px' }}>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={() => logout({ returnTo: window.location.origin })}
            >
              Logout
            </Button>
          </Stack>
        </Stack>
      </StyledBackdrop>
    )
  }

  return (
    <DashboardGlobalStateContext.Provider value={{ machines: machinesList }}>
      <App>
        <SidePanel>
          <SidePanelInnerDiv>
            <NavBox>
              {SidebarData.map((item, index) => (
                <MenuItem key={index} item={item} />
              ))}
            </NavBox>
            <Divider sx={{ margin: '4px 0', backgroundColor: 'gray' }} />
            <NavBox>
              {historicalData.map((item, index) => (
                <MenuItem key={index} item={item} />
              ))}
            </NavBox>
            <Divider sx={{ margin: '4px 0', backgroundColor: 'gray' }} />
            <NavBox>
              <RefreshDataButton onRefresh={() => window.location.reload()} />
            </NavBox>
            <LogoBox>
              <Link to="/">
                <LogoWrapper src={Logo} alt="company logo" />
              </Link>
            </LogoBox>
          </SidePanelInnerDiv>
        </SidePanel>
        <MainContent>
          <Outlet />
        </MainContent>
      </App>
    </DashboardGlobalStateContext.Provider>
  )
}

export default DashboardLayout
